import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  useTheme,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'

import { getIsACH, getIsCreditCard } from 'utils/takePayments'

import T from 'T'
import HHTextField from 'components/form-fields/v5/HHTextField'
import RenderCreditCardFields from 'components/payments/RenderCreditCardFields'
import RenderACHFields from 'components/payments/RenderACHFields'
import { PAYMENT_METHODS } from 'settings/constants/payment'

const { CREDIT_CARD, ACH } = PAYMENT_METHODS

const RenderPaymentForm = ({
  isLoading = false,
  formFields = {},
  onChange,
  onChangeBulk,
  onSave,
  showSaveButton = true,
  showSaveCheckbox = false,
}) => {
  const theme = useTheme()
  const { accountToken, cardHolderName, cardNickName, accountType, type, saveMethod = false } = formFields
  const cardHolderNameInputRef = useRef()
  const [cardHolderNameError, setCardHolderNameError] = useState()
  const [showInvalidFormAlert, setShowInvalidFormAlert] = useState(false)
  const isCreditCardForm = getIsCreditCard(type)
  const isACHForm = getIsACH(type)

  const validateCardHolderName = () => {
    if (!isCreditCardForm) return true
    const isValid = Boolean(cardHolderName)
    setCardHolderNameError(isValid ? null : T.CANNOT_BE_EMPTY)
    if (!isValid) cardHolderNameInputRef.current.focus()
    return isValid
  }

  const handleSave = () => {
    const isCardHolderValid = validateCardHolderName()
    if (!isCardHolderValid) return
    const isTokenValid = Boolean(accountToken)
    setShowInvalidFormAlert(!isTokenValid)
    if (!isTokenValid) return
    onSave()
  }

  return (
    <>
      <Grid item container xs={12} columnSpacing={2}>
        <Grid container item xs={12} sm={12} md={12} lg={12} xl={12} columnSpacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
            <ToggleButtonGroup
              sx={{
                mb: 2,
                width: '100%',
                display: 'flex',
                height: 40,
                backgroundColor: theme.palette.action.hover,
                borderRadius: 2.5,
                p: 0.5,
                border: `1px solid ${theme.palette.action.disabledBackground}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.06) inset, 0 1px 1px rgba(0,0,0,0.04)',
                [`& .${toggleButtonGroupClasses.grouped}`]: {
                  border: 0,
                  margin: 0,
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: theme.palette.text.disabled,
                  '&:not(.Mui-selected)': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-selected': {
                    color: theme.palette.common.white,
                    backgroundColor: theme.palette.primary.main,
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.5) inset',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                  [`&.${toggleButtonGroupClasses.disabled}`]: {
                    border: 0,
                  },
                },
              }}
              color="primary"
              value={type}
              exclusive
              onChange={(event, newType) => {
                if (newType !== null) onChange('type', newType)
              }}
              aria-label="Platform"
            >
              <ToggleButton
                value={CREDIT_CARD}
                autoFocus={type === CREDIT_CARD}
                sx={{
                  py: 1.1,
                  '&.Mui-focusVisible, &:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {T.CREDIT_CARD}
              </ToggleButton>
              <ToggleButton
                value={ACH}
                autoFocus={type === ACH}
                sx={{
                  py: 1.1,
                  '&.Mui-focusVisible, &:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {T.ACH}
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6} xl={6} />
        </Grid>
        <Grid container item xs={12} sm={12} md={12} lg={6} xl={6}>
          <Grid item xs={12} />
          {isCreditCardForm && (
            <RenderCreditCardFields
              cardHolderName={cardHolderName}
              showInvalidFormAlert={showInvalidFormAlert}
              onChange={onChange}
              onNewCardTokenChange={onChangeBulk}
              cardHolderNameError={cardHolderNameError}
              cardHolderNameInputRef={cardHolderNameInputRef}
            />
          )}
          {isACHForm && (
            <RenderACHFields
              cardHolderName={cardHolderName}
              showInvalidFormAlert={showInvalidFormAlert}
              onChange={onChange}
              onNewCardTokenChange={onChangeBulk}
              accountType={accountType}
            />
          )}
        </Grid>
        <Grid container item xs={12} sm={12} md={12} lg={6} xl={6}>
          <Box sx={{ width: '100%' }}>
            <HHTextField
              label={T.METHOD_NICKNAME}
              placeholder={T.METHOD_NICKNAME}
              fullWidth
              value={cardNickName}
              onChange={event => onChange('cardNickName', event.target.value)}
            />
          </Box>
        </Grid>
        {showSaveButton && (
          <Grid item xs={6}>
            <LoadingButton sx={{ mt: 2 }} fullWidth loading={isLoading} onClick={handleSave} variant="contained">
              {T.SAVE_METHOD}
            </LoadingButton>
          </Grid>
        )}
        {showSaveCheckbox && (
          <Grid item xs={6}>
            <FormControl sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    tabIndex={0}
                    checked={Boolean(saveMethod)}
                    onChange={event => onChange('saveMethod', event.target.checked)}
                    inputProps={{ 'aria-label': T.SAVE_METHOD }}
                    sx={{
                      '&.Mui-focusVisible, &:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                        borderRadius: 2,
                      },
                    }}
                  />
                }
                label={T.SAVE_METHOD}
              />
            </FormControl>
          </Grid>
        )}
      </Grid>
    </>
  )
}
RenderPaymentForm.propTypes = {
  isLoading: PropTypes.bool,
  formFields: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onChangeBulk: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  showSaveButton: PropTypes.bool,
  showSaveCheckbox: PropTypes.bool,
}

export default RenderPaymentForm
